import { Controller } from '@hotwired/stimulus'
import fetchWithHandlingLoginRedirect from '../../modules/utils/fetchWithHandlingLoginRedirect'
import { trans, DELETE_FAILED_TOAST } from '../../translator'

// fetchWithHandlingLoginRedirect resolves only on a 2xx response and rejects otherwise.
const XHR_HEADERS = { 'X-Requested-With': 'XMLHttpRequest' }

/**
 * Confirms and performs a soft-delete of an Associated Person from the Show screen. Clicking
 * Delete opens a confirmation modal; confirming issues DELETE against the session-authed
 * internal endpoint and, on a 204, redirects to the index with a query param the index reads
 * to surface a "Record deleted" toast. A failed delete keeps the modal open with an error.
 */
export default class DeleteController extends Controller {
  static targets = ['modal', 'confirmButton', 'error']
  static values = {
    deleteUrl: String,
    indexUrl: String
  }

  declare readonly modalTarget: HTMLElement
  declare readonly hasModalTarget: boolean
  declare readonly confirmButtonTarget: HTMLButtonElement
  declare readonly hasConfirmButtonTarget: boolean
  declare readonly errorTarget: HTMLElement
  declare readonly hasErrorTarget: boolean
  declare readonly deleteUrlValue: string
  declare readonly indexUrlValue: string

  openModal (): void {
    this.clearError()
    if (this.hasModalTarget) {
      this.modalTarget.classList.add('show')
      this.modalTarget.style.display = 'block'
      document.body.classList.add('modal-open')
    }
  }

  closeModal (): void {
    if (this.hasModalTarget) {
      this.modalTarget.classList.remove('show')
      this.modalTarget.style.display = 'none'
      document.body.classList.remove('modal-open')
    }
  }

  async confirm (): Promise<void> {
    if (this.hasConfirmButtonTarget) {
      this.confirmButtonTarget.disabled = true
    }

    try {
      await fetchWithHandlingLoginRedirect(this.deleteUrlValue, { method: 'DELETE', headers: XHR_HEADERS })
      window.location.assign(`${this.indexUrlValue}?deleted=1`)
    } catch {
      if (this.hasConfirmButtonTarget) {
        this.confirmButtonTarget.disabled = false
      }
      this.showError(trans(DELETE_FAILED_TOAST, {}, 'associated-persons'))
    }
  }

  private showError (message: string): void {
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = message
      this.errorTarget.classList.remove('d-none')
    }
  }

  private clearError (): void {
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = ''
      this.errorTarget.classList.add('d-none')
    }
  }
}
